import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, Users, Shield, UserPlus, X, Trash2 } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { AddResourcePermissionDialog } from '@/components/permissions/AddResourcePermissionDialog'
import { PermissionTabs } from '@/components/permissions/PermissionTabs'
import {
  indexCatalog,
  instanceGrantableActions,
  useResourceNameResolver,
  type ScopedGrantRow,
} from '@/components/permissions/permissionCatalog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useAllowedActions,
  usePermission,
  usePermissionCatalog,
  invalidatePermissionQueries,
} from '@/hooks/usePermission'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import {
  getRole,
  getRolePermissions,
  replaceRolePermissions,
  grantRolePermission,
  revokeRoleGrant,
  getRoleMembers,
  deleteRole,
} from '@/services/api/permission.service'
import { getWorkspaceUsers, assignRole, removeRole } from '@/services/api/workspace.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { WorkspaceUser } from '@/types/api/workspace'
import { RoleType } from '@/types/enums'
import { logger } from '@/utils/logger'


export function RoleDetailPage() {
  const { roleId } = useParams<{ roleId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can, isOwner, isAdmin } = usePermission()
  const roleActions = useAllowedActions('ROLE', roleId)

  const { data: role, isLoading: roleLoading } = useQuery({
    queryKey: ['roles', roleId],
    queryFn: () => getRole(roleId!),
    enabled: !!roleId,
  })
  const { data: permissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ['roles', roleId, 'permissions'],
    queryFn: () => getRolePermissions(roleId!),
    enabled: !!roleId,
  })
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['roles', roleId, 'members'],
    queryFn: () => getRoleMembers(roleId!),
    enabled: !!roleId,
  })
  const { data: workspaceUsers = [] } = useQuery({ queryKey: ['workspace-users'], queryFn: getWorkspaceUsers })
  const { data: catalog } = usePermissionCatalog()
  const index = useMemo(() => (catalog ? indexCatalog(catalog) : null), [catalog])
  const { recordGroups = [] } = useRecordGroups()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [hasChanges, setHasChanges] = useState(false)
  const [addCategory, setAddCategory] = useState<string | null>(null)
  const [memberToAdd, setMemberToAdd] = useState('')
  const [memberToRemove, setMemberToRemove] = useState<WorkspaceUser | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const privileged = isOwner || isAdmin
  const isAdminRole = role?.roleType === RoleType.ADMIN
  const canManagePermissions = roleActions.can(PERMISSION_KEYS.ROLE_MANAGE_PERMISSIONS) && !isAdminRole
  const canManageMembers = roleActions.can(PERMISSION_KEYS.ROLE_MANAGE_USERS) || can(PERMISSION_KEYS.USER_MANAGE_ROLES)
  const canDelete = roleActions.can(PERMISSION_KEYS.ROLE_DELETE) && (!isAdminRole || privileged)
  // Escalation guard mirror: non-admins may only grant what they hold (the API enforces it).
  const canGrantKey = (key: string) => privileged || can(key)

  useEffect(() => {
    if (permissions) {
      setSelected(new Set(permissions.filter((p) => p.scope === 'GLOBAL').map((p) => p.permissionKey)))
      setHasChanges(false)
    }
  }, [permissions])

  const scopedGrants: ScopedGrantRow[] = useMemo(
    () =>
      (permissions ?? [])
        .filter((p) => p.scope && p.scope !== 'GLOBAL')
        .map((p) => ({
          rowKey: p.id,
          permissionKey: p.permissionKey,
          resourceType: p.resourceType ?? '',
          resourceId: p.resourceId,
          scope: p.scope!,
          sources: [],
          revokeGrantId: p.id,
        })),
    [permissions]
  )
  const resolveName = useResourceNameResolver(scopedGrants)

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['roles', roleId] })
    invalidatePermissionQueries(queryClient)
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      replaceRolePermissions(roleId!, {
        permissions: [
          ...scopedGrants.map((g) => ({
            permissionKey: g.permissionKey,
            resourceType: g.resourceType,
            resourceId: g.resourceId,
            scope: g.scope,
          })),
          ...Array.from(selected).map((key) => ({ permissionKey: key, scope: 'GLOBAL' as const, resourceId: null })),
        ],
      }),
    onSuccess: () => {
      refresh()
      toast.success('Permissions saved')
      setHasChanges(false)
    },
  })

  const grantMutation = useMutation({
    mutationFn: (data: { permissionKey: string; resourceType: string; resourceId: string; scope: string }) =>
      grantRolePermission({ granteeRoleId: roleId!, ...data, scope: data.scope as never }),
    onSuccess: () => {
      refresh()
      toast.success('Access granted')
      setAddCategory(null)
    },
    onError: (err) => logger.error('Failed to grant permission', err, 'expected'),
  })

  const revokeMutation = useMutation({
    mutationFn: (row: ScopedGrantRow) => revokeRoleGrant(roleId!, row.revokeGrantId!),
    onSuccess: () => {
      refresh()
      toast.success('Access revoked')
    },
  })

  const addMemberMutation = useMutation({
    mutationFn: (userId: string) => assignRole(userId, roleId!),
    onSuccess: () => {
      setMemberToAdd('')
      queryClient.invalidateQueries({ queryKey: ['roles', roleId, 'members'] })
      queryClient.invalidateQueries({ queryKey: ['workspace-users'] })
      invalidatePermissionQueries(queryClient)
      toast.success('Member added')
    },
  })

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => removeRole(userId, roleId!),
    onSuccess: () => {
      setMemberToRemove(null)
      queryClient.invalidateQueries({ queryKey: ['roles', roleId, 'members'] })
      queryClient.invalidateQueries({ queryKey: ['workspace-users'] })
      invalidatePermissionQueries(queryClient)
      toast.success('Member removed')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteRole(roleId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      invalidatePermissionQueries(queryClient)
      toast.success('Role deleted')
      navigate('/roles')
    },
  })

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    setHasChanges(true)
  }

  const toggleMany = (keys: string[], value: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      keys.forEach((k) => (value ? next.add(k) : next.delete(k)))
      return next
    })
    setHasChanges(true)
  }

  const memberIds = new Set(members.map((m) => m.userId))
  const candidates = workspaceUsers.filter((u) => !memberIds.has(u.userId))

  if (roleLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!role) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h2 className="text-lg font-medium">Role not found</h2>
          <Button variant="link" onClick={() => navigate('/roles')}>Back to Roles</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/roles')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Roles
        </Button>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold sm:text-3xl">{role.name}</h1>
              <Badge variant="secondary">{role.roleType}</Badge>
              {!role.active && <Badge variant="outline">Inactive</Badge>}
            </div>
            <p className="mt-1 text-neutral-500">{role.description || 'No description'}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canDelete && (
              <Button variant="outline" onClick={() => setConfirmDelete(true)} className="text-red-600 hover:text-red-700">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete role
              </Button>
            )}
            {canManagePermissions && (
              <Button onClick={() => saveMutation.mutate()} disabled={!hasChanges || saveMutation.isPending}>
                <Save className="mr-2 h-4 w-4" />
                {saveMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Permissions
          </CardTitle>
          <CardDescription>
            {isAdminRole
              ? 'Admin roles have every permission except billing; there is nothing to configure.'
              : canManagePermissions
                ? 'Choose what members of this role can do, workspace-wide or on specific items.'
                : 'You can view but not change this role’s permissions.'}
          </CardDescription>
        </CardHeader>
        {!isAdminRole && (
          <CardContent>
            {permissionsLoading || !index ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : (
              <PermissionTabs
                index={index}
                selected={selected}
                onToggle={toggle}
                onToggleMany={toggleMany}
                scopedGrants={scopedGrants}
                resolveName={resolveName}
                onRevoke={(row) => revokeMutation.mutate(row)}
                onAddScoped={setAddCategory}
                canGrantKey={canGrantKey}
                readOnly={!canManagePermissions}
                revoking={revokeMutation.isPending}
              />
            )}
          </CardContent>
        )}
      </Card>

      {index && (
        <AddResourcePermissionDialog
          open={addCategory !== null}
          onOpenChange={(open) => !open && setAddCategory(null)}
          options={addCategory ? instanceGrantableActions(index, addCategory) : []}
          recordGroups={recordGroups}
          onSave={(data) => grantMutation.mutate(data)}
          isSaving={grantMutation.isPending}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Members
            <Badge variant="secondary">{members.length}</Badge>
          </CardTitle>
          <CardDescription>People who hold this role in the workspace.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {canManageMembers && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <select
                value={memberToAdd}
                onChange={(e) => setMemberToAdd(e.target.value)}
                className="w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm outline-none focus:border-primary-500 sm:max-w-sm"
                aria-label="Member to add"
              >
                <option value="">Choose a member to add…</option>
                {candidates.map((u) => (
                  <option key={u.userId} value={u.userId}>{u.name} ({u.email})</option>
                ))}
              </select>
              <Button
                onClick={() => addMemberMutation.mutate(memberToAdd)}
                disabled={!memberToAdd || addMemberMutation.isPending}
                className="gap-1.5"
              >
                <UserPlus className="h-4 w-4" />
                Add member
              </Button>
            </div>
          )}

          {membersLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : members.length === 0 ? (
            <p className="text-sm text-neutral-500">Nobody holds this role yet.</p>
          ) : (
            <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
              {members.map((user) => (
                <div key={user.userId} className="flex items-center justify-between gap-3 p-3">
                  <button
                    type="button"
                    className="min-w-0 text-left"
                    onClick={() => navigate(`/users/${user.userId}`)}
                  >
                    <p className="truncate font-medium">{user.name}</p>
                    <p className="truncate text-sm text-neutral-500">{user.email}</p>
                  </button>
                  {canManageMembers && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMemberToRemove(user)}
                      className="shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <X className="mr-1 h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!memberToRemove}
        onOpenChange={(open) => !open && setMemberToRemove(null)}
        title={`Remove ${memberToRemove?.name ?? ''} from ${role.name}?`}
        description="They lose every permission that comes only from this role. Permissions they hold through other roles or personal overrides stay."
        confirmLabel="Remove member"
        destructive
        pending={removeMemberMutation.isPending}
        onConfirm={() => memberToRemove && removeMemberMutation.mutate(memberToRemove.userId)}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete the ${role.name} role?`}
        description={`Its ${members.length} member(s) lose the permissions this role grants, and the role's grants are removed. This cannot be undone.`}
        confirmLabel="Delete role"
        destructive
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  )
}
